/**
 * Play-test harness shell.
 *
 * A `NativeShell` used only when this client runs inside the dev-only play-test
 * wrapper (see `src/playtest/`). Instead of real device sensors it gets its
 * world from the parent window over `postMessage`:
 *
 *   parent → iframe  { wh:'gps',   lat, lng, heading, speed }   → `location` event
 *   parent → iframe  { wh:'audio', enabled }                    → master mute
 *   iframe → parent  { wh:'ready', clientId }                   → handshake
 *   iframe → parent  { wh:'store', clientId, key, value }       → persist a write
 *
 * Identity / storage: several iframes share one origin, so plain `localStorage`
 * would make every client log in as the same user. Instead the *parent* owns
 * persistence, keyed by each client's stable id (`pt1`, `pt2`, …): it bakes that
 * client's saved key/value blob into the iframe URL as `?store=<json>` (so the
 * shell has a synchronous snapshot at boot — no async getItem, mirroring how a
 * real native shell hands its storage up-front), and writes are posted back via
 * `wh:'store'`. So the Nth client always logs back in as the Nth client's user
 * across iframe reloads, page refreshes and restarts.
 *
 * Speech is reported as unavailable (tap the on-screen actions to test); GPS is
 * driven entirely by the harness; keep-awake / loadApp are no-ops.
 */
import type { NativeShell, NativeEvent, GeoFix, ShellCapabilities } from './types';
import { setAudioMuted } from '../audio/context';

interface HarnessMessage {
  wh?: 'gps' | 'audio';
  lat?: number;
  lng?: number;
  heading?: number;
  speed?: number;
  enabled?: boolean;
}

export function createHarnessShell(clientId: string): NativeShell {
  const handlers = new Set<(e: NativeEvent) => void>();
  const emit = (e: NativeEvent) => {
    for (const h of handlers) h(e);
  };

  let lastFix: GeoFix | null = null;
  let locating = false;

  // Mirror this iframe's console + uncaught errors up to the play-test harness.
  installConsoleRelay(clientId);

  // Credential/storage snapshot, baked into this iframe's URL by the parent
  // (?store=<urlencoded json>) so it's available synchronously at boot — see
  // file header. Writes are posted back to the parent, which owns persistence.
  const storage: Record<string, string> = (() => {
    try {
      const raw = new URLSearchParams(location.search).get('store');
      const v = raw ? (JSON.parse(raw) as unknown) : null;
      return v && typeof v === 'object' ? (v as Record<string, string>) : {};
    } catch {
      return {};
    }
  })();

  // Default to muted; the parent un-mutes whichever iframe is "soloed".
  setAudioMuted(true);

  const announce = () => window.parent?.postMessage({ wh: 'ready', clientId }, '*');

  window.addEventListener('message', (ev: MessageEvent<HarnessMessage>) => {
    const d = ev.data;
    if (!d || !d.wh) return;
    if (d.wh === 'gps' && typeof d.lat === 'number' && typeof d.lng === 'number') {
      lastFix = {
        lat: d.lat,
        lng: d.lng,
        accuracy: 5,
        speed: d.speed ?? 0,
        heading: d.heading ?? 0,
      };
      if (locating) emit({ type: 'location', fix: lastFix });
    } else if (d.wh === 'audio') {
      setAudioMuted(!d.enabled);
    }
  });

  const caps: ShellCapabilities = {
    speech: false,
    location: true,
    keepAwake: false,
    storage: true,
    platform: 'harness',
  };

  // Let the parent know we exist so it sends our initial position + audio state.
  announce();

  return {
    capabilities: () => caps,
    subscribe(handler) {
      handlers.add(handler);
      return () => handlers.delete(handler);
    },
    startSpeech() {
      /* no speech in the harness — tap actions instead */
    },
    stopSpeech() {
      /* noop */
    },
    setAudioPlaying() {
      /* noop */
    },
    getAudioDebug() {
      return "";
    },
    startLocation() {
      locating = true;
      // Emit the last known fix immediately, or re-announce so the parent
      // pushes one (e.g. if startLocation ran before the first gps message).
      if (lastFix) emit({ type: 'location', fix: lastFix });
      else announce();
    },
    stopLocation() {
      locating = false;
    },
    setKeepAwake() {
      /* noop */
    },
    storage,
    setItem: (key, value) => {
      // The parent persists per-client (keyed by clientId) to its localStorage.
      window.parent?.postMessage({ wh: 'store', clientId, key, value }, '*');
    },
    loadApp: () => {
      /* single served version in the harness */
    },
    showToast: (message) => console.log(`[harness ${clientId}] ${message}`),
  };
}

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

/** Best-effort, readable preview of a single console argument. */
function previewArg(a: unknown): string {
  if (typeof a === 'string') return a;
  if (a instanceof Error) return a.stack || `${a.name}: ${a.message}`;
  try {
    return JSON.stringify(a);
  } catch {
    return String(a);
  }
}

/**
 * Patch this iframe's `console` and uncaught-error handlers to forward every
 * message to the parent play-test harness ({ wh:'console', clientId, level,
 * text }). The original console methods are still called, so the iframe's own
 * devtools keep working.
 */
function installConsoleRelay(clientId: string): void {
  const send = (level: LogLevel, text: string) => {
    try {
      window.parent?.postMessage({ wh: 'console', clientId, level, text: text.slice(0, 4000) }, '*');
    } catch {
      /* noop */
    }
  };
  const c = console as unknown as Record<LogLevel, (...args: unknown[]) => void>;
  for (const level of ['log', 'info', 'warn', 'error', 'debug'] as LogLevel[]) {
    const orig = typeof c[level] === 'function' ? c[level].bind(console) : () => {};
    c[level] = (...args: unknown[]) => {
      orig(...args);
      send(level, args.map(previewArg).join(' '));
    };
  }
  window.addEventListener('error', (e: ErrorEvent) => {
    const where = e.filename ? ` (${e.filename}:${e.lineno}:${e.colno})` : '';
    const stack = e.error instanceof Error && e.error.stack ? `\n${e.error.stack}` : '';
    send('error', `${e.message}${where}${stack}`);
  });
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    send('error', 'Unhandled rejection: ' + previewArg(e.reason));
  });
}
